/**
 * QR placeholder markers — the shared, dependency-free half of the QR
 * preview story.
 *
 * `parse-email.ts:parseQRCode` serializes a QRCode craft node to an
 * `<img>` whose `src` is a backend-only marker, resolved server-side at
 * send / render time (main-consumer `qr-placeholder.ts`). Two forms:
 *   {{qrCodeImage}}        — the guest's signed check-in token
 *   {{qrCodeImage:VALUE}}  — VALUE verbatim, with nested {{var}} resolved
 *
 * Anything that renders that HTML in the browser without the backend must
 * substitute the markers first, or they reach the DOM as a relative URL
 * and paint a broken-image glyph.
 *
 * This module deliberately imports NOTHING: `qr-preview.ts` (the async
 * resolver that mints real codes) pulls in the `qrcode` library, and the
 * document presets' `_helpers` pulls in `parse-email.ts`. The template
 * GALLERY needs neither — it only needs the marker grammar and a
 * look-alike tile — so both live here and the heavy modules import from
 * this one rather than the other way round.
 */

export const QR_PLAIN_MARKER = "{{qrCodeImage}}";
export const QR_CUSTOM_OPENER = "{{qrCodeImage:";

/**
 * The STYLED marker family — same two forms, plus the author's colours:
 *   {{qrCodeImageStyled:RRGGBBAA-RRGGBBAA}}        — guest QR, fg-bg
 *   {{qrCodeImageStyled:RRGGBBAA-RRGGBBAA:VALUE}}  — custom value, fg-bg
 *
 * Why a SEPARATE opener rather than an argument on the existing one: every
 * expander (backend `QrPlaceholderExpander`, consumer `qr-placeholder.ts`, the
 * two preview lanes here) scans for the literal `{{qrCodeImage:` / matches
 * `{{qrCodeImage}}` exactly. `{{qrCodeImageStyled:` contains neither, so the
 * new form is invisible to the old scanners instead of half-matching them —
 * and every template stored before this keeps rendering byte-identically.
 *
 * The emitter only reaches for it when the author moved a colour off
 * black-on-white; the default stays on the legacy marker.
 *
 * Colours are 8 hex digits (RRGGBBAA) so transparency travels as alpha and the
 * whole marker stays inside `[a-zA-Z0-9.:_-]` — the shape the variable linter
 * and the backend sanitizer's whole-value-marker test already accept.
 */
export const QR_STYLED_OPENER = "{{qrCodeImageStyled:";

/**
 * The render options a styled marker carries: the colour pair as 8-digit
 * RRGGBBAA, plus the error-correction level.
 *
 * `ecc` is part of the marker for the same reason the colours are: the settings
 * panel offers L/M/Q/H, the canvas honours it (`QRCode.tsx` passes `level`), and
 * every renderer hard-coded `M`. An author who picks H for a badge that will be
 * scanned off a lanyard got a code with materially less damage tolerance, with
 * nothing saying so.
 */
export type QrStyle = { fg: string; bg: string; ecc: QrEcc };
export type QrEcc = "L" | "M" | "Q" | "H";

/** Black on white at ECC M — what the legacy (unstyled) markers mean. */
export const QR_DEFAULT_STYLE: QrStyle = { fg: "000000FF", bg: "FFFFFFFF", ecc: "M" };

/**
 * The quiet zone every renderer draws INSIDE the barcode, in modules — the
 * ISO 18004 minimum of four on every side. Pinned to main-consumer
 * `qr-placeholder.ts` (`QR_MARGIN`) and main-backend `QrPngGenerator`
 * (`GUEST_QR_MARGIN`); the canvas (`QRCode.tsx`) and the send preview
 * (`qr-preview.ts`) read it from here so the author sees the module density
 * the guest receives. It was one module, which a phone camera on a busy
 * ticket background routinely failed to lock on to.
 */
export const QR_QUIET_ZONE_MODULES = 4;

/**
 * The white field the author can switch on behind the code, in CSS pixels of
 * padding around the image. One value for the canvas and the stored HTML
 * (`parse-email.ts:parseQRCode`), so the two agree to the pixel.
 */
export const QR_WHITE_FIELD_PADDING_PX = 8;

/**
 * What a guest-QR block encodes when no real token is at hand — the canvas,
 * and the send preview when the token endpoint refuses. Shaped like the real
 * token the mail and the PDF carry — `<24 hex guestId>.<16 base64url
 * signature>`, 41 chars → QR version 3, 29 modules — so the mock shows the
 * same module density the guest receives. Not a real signature; a scan of it
 * is meant to fail.
 */
export const QR_GUEST_SAMPLE_VALUE = "6a4f8b2e91d34c07a5e63d8f.k3JmQ2xVn8pTz1Ws";

const RGBA8 = /^[0-9A-Fa-f]{8}$/;
const ECC_CHARS = "LMQH";

/** `#rrggbbaa` for a CSS consumer (the preview lanes render in a browser). */
export function qrStyleToCssHex(rgba8: string): string {
  return `#${rgba8.toLowerCase()}`;
}

/**
 * Undo the ONE attribute-escaping layer `parseQRCode` applies to the marker
 * argument, so a preview encodes what the author typed.
 *
 * The marker is scanned out of RAW HTML SOURCE, so nothing else undoes the
 * escaping the emitter must apply to survive inside `src="…"`. Ported from the
 * two expanders (`main-backend` QrPlaceholderExpander.decodeHtmlEntities,
 * `main-consumer` qr-placeholder.ts) — if this drifts, the send preview shows a
 * different code than the mail carries. `&amp;` LAST so `&amp;lt;` decodes to
 * `&lt;` rather than over-decoding to `<`.
 */
export function decodeQrMarkerValue(value: string): string {
  if (!value.includes("&")) return value;
  return value
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&");
}

/**
 * True when a QR value can sit inside a `{{qrCodeImage:…}}` marker without
 * breaking its brace-balanced grammar.
 *
 * All three expanders (backend, consumer, and the FE preview) scan for the
 * terminator by counting `{{`/`}}` pairs, so a typed `}}` ends the marker early
 * — the barcode gets a truncated payload and the rest of the value leaks into
 * the HTML as text. A stray `{{` unbalances the scan and the marker is
 * abandoned entirely. Neither surfaces anywhere today.
 *
 * Nested `{{variable}}` references are legitimate and stay legal; only
 * UNBALANCED braces are refused.
 */
export function isBalancedQrValue(value: string): boolean {
  let depth = 0;
  for (let i = 0; i < value.length; i++) {
    if (value.startsWith("{{", i)) {
      depth++;
      i++;
    } else if (value.startsWith("}}", i)) {
      depth--;
      if (depth < 0) return false;
      i++;
    }
  }
  return depth === 0;
}

/*
 * The stored-HTML damage check that used to live here now lives in
 * `qr-style.ts` as `storedHtmlNeedsQrRegen`. It had to move: deciding whether a
 * page's markers are intact requires knowing which marker family each QR node
 * EMITS, which depends on its colours, and this module stays free of the colour
 * parser on purpose (the template gallery imports it for the marker grammar
 * alone). The per-document version that briefly lived here also answered the
 * wrong question — see the note on `storedHtmlNeedsQrRegen`.
 */

/**
 * Brace-balanced scan for every {{qrCodeImage:<VALUE>}} occurrence where
 * VALUE may itself contain nested {{var}} references. Ported verbatim
 * from main-consumer `qr-placeholder.ts` so the FE preview matches the
 * backend's match semantics exactly.
 */
export function findQrCustomMatches(
  html: string,
): Array<{ match: string; value: string }> {
  const out: Array<{ match: string; value: string }> = [];
  let i = 0;
  while (i < html.length) {
    const start = html.indexOf(QR_CUSTOM_OPENER, i);
    if (start < 0) break;
    const valueStart = start + QR_CUSTOM_OPENER.length;
    let depth = 1;
    let j = valueStart;
    while (j < html.length - 1) {
      if (html[j] === "{" && html[j + 1] === "{") {
        depth++;
        j += 2;
      } else if (html[j] === "}" && html[j + 1] === "}") {
        depth--;
        if (depth === 0) break;
        j += 2;
      } else {
        j++;
      }
    }
    if (depth === 0) {
      const value = html.substring(valueStart, j);
      out.push({ match: html.substring(start, j + 2), value });
      i = j + 2;
    } else {
      // Unbalanced — resume after the opener so a later balanced
      // occurrence still gets picked up.
      i = start + QR_CUSTOM_OPENER.length;
    }
  }
  return out;
}

/**
 * Brace-balanced scan for every STYLED marker. Returns the colour pair plus
 * `value === null` for the plain (guest-QR) form.
 *
 * Same depth-counting scan as `findQrCustomMatches` — a custom value may hold
 * nested `{{var}}` references — with the colour pair read off the front. A
 * malformed colour pair is NOT a match: the marker is left alone so it fails
 * visibly rather than silently rendering with invented colours.
 *
 * Ported line-for-line into `main-consumer/src/lib/qr-placeholder.ts` and
 * `main-backend` `QrPlaceholderExpander`; the three must agree exactly or the
 * preview shows a different code than the mail and the PDF carry.
 */
export function findQrStyledMatches(
  html: string,
): Array<{ match: string; style: QrStyle; value: string | null }> {
  const out: Array<{ match: string; style: QrStyle; value: string | null }> = [];
  let i = 0;
  while (i < html.length) {
    const start = html.indexOf(QR_STYLED_OPENER, i);
    if (start < 0) break;
    const styleStart = start + QR_STYLED_OPENER.length;
    // RRGGBBAA-RRGGBBAA is fixed-width: 8 + 1 + 8, then an OPTIONAL `-E` for the
    // error-correction level. Optional so a marker written before ECC travelled
    // still parses — it simply means the pinned default, M.
    const fg = html.substring(styleStart, styleStart + 8);
    const dash = html[styleStart + 8];
    const bg = html.substring(styleStart + 9, styleStart + 17);
    let after = styleStart + 17;
    if (!RGBA8.test(fg) || dash !== "-" || !RGBA8.test(bg)) {
      i = styleStart;
      continue;
    }
    let ecc: QrEcc = "M";
    if (html[after] === "-" && ECC_CHARS.includes(html[after + 1] ?? "")) {
      ecc = html[after + 1] as QrEcc;
      after += 2;
    }
    const style: QrStyle = { fg: fg.toUpperCase(), bg: bg.toUpperCase(), ecc };
    if (html.startsWith("}}", after)) {
      out.push({ match: html.substring(start, after + 2), style, value: null });
      i = after + 2;
      continue;
    }
    if (html[after] !== ":") {
      i = styleStart;
      continue;
    }
    const valueStart = after + 1;
    let depth = 1;
    let j = valueStart;
    while (j < html.length - 1) {
      if (html[j] === "{" && html[j + 1] === "{") {
        depth++;
        j += 2;
      } else if (html[j] === "}" && html[j + 1] === "}") {
        depth--;
        if (depth === 0) break;
        j += 2;
      } else {
        j++;
      }
    }
    if (depth === 0) {
      out.push({
        match: html.substring(start, j + 2),
        style,
        value: html.substring(valueStart, j),
      });
      i = j + 2;
    } else {
      i = styleStart;
    }
  }
  return out;
}

/** True when `html` carries any QR marker at all — legacy or styled. */
export function hasQrMarker(html: string): boolean {
  return (
    html.includes(QR_PLAIN_MARKER) ||
    html.includes(QR_CUSTOM_OPENER) ||
    html.includes(QR_STYLED_OPENER)
  );
}

/**
 * A QR-code-shaped SVG for gallery preview thumbnails — three finder
 * patterns plus a deterministic module fill. Not a real (scannable)
 * code: the live ticket renders a QRCode craft node bound to the
 * guest's `guestQr`. This only exists so the preview card looks like
 * a finished ticket instead of an empty white square.
 */
export function qrPreviewSvg(
  px: number,
  fg = "#000000",
  bg = "#ffffff",
): string {
  const N = 21;
  const c = 100 / N;
  const cell = (x: number, y: number) =>
    `<rect x="${(x * c).toFixed(2)}" y="${(y * c).toFixed(2)}" width="${c.toFixed(2)}" height="${c.toFixed(2)}"/>`;
  const inFinder = (x: number, y: number) =>
    (x < 8 && y < 8) || (x > 12 && y < 8) || (x < 8 && y > 12);
  let m = "";
  const finder = (ox: number, oy: number) => {
    for (let y = 0; y < 7; y++)
      for (let x = 0; x < 7; x++) {
        const edge = x === 0 || x === 6 || y === 0 || y === 6;
        const core = x >= 2 && x <= 4 && y >= 2 && y <= 4;
        if (edge || core) m += cell(ox + x, oy + y);
      }
  };
  finder(0, 0);
  finder(14, 0);
  finder(0, 14);
  for (let y = 0; y < N; y++)
    for (let x = 0; x < N; x++) {
      if (inFinder(x, y)) continue;
      if ((x * 5 + y * 3 + x * y * 2) % 7 < 3) m += cell(x, y);
    }
  return (
    `<svg width="${px}" height="${px}" viewBox="0 0 100 100" ` +
    `xmlns="http://www.w3.org/2000/svg" style="display:block">` +
    `<rect width="100" height="100" fill="${bg}"/>` +
    `<g fill="${fg}">${m}</g></svg>`
  );
}

/**
 * The look-alike tile every thumbnail substitutes in. Same recipe the
 * document presets already used for their (now-unreachable) previewHtml.
 *
 * `encodeURIComponent` escapes `"`, `<`, `>` and `&`, so the result is
 * safe verbatim inside a double-quoted `src` attribute and — because it
 * contains no `>` — cannot break `lazifyPreviewImages`' lookahead.
 */
export const QR_THUMBNAIL_URI =
  "data:image/svg+xml;utf8," + encodeURIComponent(qrPreviewSvg(160));

/**
 * Synchronously replace every {{qrCodeImage(:VALUE)?}} marker with the
 * look-alike tile. For preview surfaces that have no guest and no
 * backend — the template galleries, their full-size preview modal and
 * the wizard's live preview — where a real, scannable code is neither
 * available nor meaningful.
 *
 * Contrast with `resolveQrPlaceholdersForPreview` (qr-preview.ts), which
 * is async and mints a REAL code from a signed guest token; use that one
 * wherever an actual guest is in hand (send preview, sample PDF).
 *
 * Returns the input unchanged when no marker is present (cheap path for
 * the 90%+ of templates without a QR block).
 */
export function resolveQrPlaceholdersForThumbnail(html: string): string {
  if (!html) return html;
  if (!hasQrMarker(html)) return html;

  let out = html;
  // Styled first: its opener is a superstring of neither legacy form, but the
  // tile it becomes carries the author's colours, so it must not be consumed
  // by the plain pass below.
  for (const { match, style } of findQrStyledMatches(out)) {
    const tile =
      "data:image/svg+xml;utf8," +
      encodeURIComponent(
        qrPreviewSvg(160, qrStyleToCssHex(style.fg), qrStyleToCssHex(style.bg)),
      );
    out = out.split(match).join(tile);
  }
  // Custom form first, mirroring the consumer's ordering. (The two forms
  // can't actually collide — `{{qrCodeImage}}` and `{{qrCodeImage:` differ
  // at the closing brace — but keeping the order identical keeps this
  // readable next to `qr-preview.ts` and the backend.)
  // Scans `out`, not `html`: the styled pass above has already rewritten its
  // own matches, and a legacy marker nested inside a styled VALUE went with it.
  // Same drift the consumer's expander had — fixed there, so fixed here.
  for (const { match } of findQrCustomMatches(out)) {
    out = out.split(match).join(QR_THUMBNAIL_URI);
  }
  return out.split(QR_PLAIN_MARKER).join(QR_THUMBNAIL_URI);
}
